import React, { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import { ISidebarMenuItemProps } from '../models';
import { Link as ScrollLink } from 'react-scroll';
import { IComponentChildrenProps } from '@src/models/props';
import { Link } from '@src/atoms/Link';
import { classnames } from '@src/utils/classnames';
import { AnyType } from '@src/models/common';
import { getClasses } from '@src/utils/getClass';

import styles from './SidebarMenuLink.module.scss';
import { Box } from '@src/atoms/Box';

interface IProps extends IComponentChildrenProps, ISidebarMenuItemProps {}

const SidebarMenuLinkRef = forwardRef<AnyType, IProps>(
  (
    {
      id,
      containerId,
      href,
      children,
      target,
      scroll,
      theme,
      className,
      exact,
      offset,
      active: activeProps,
      smooth = true,
      spy = true,
      hashSpy = true,
      disableNextJs,
      onSetActive,
      onClick,
      basePath,
      currentPathname,
      withSearch = false,
      rounded = false,
      extraHrefList,
    },
    ref,
  ) => {
    const [active, setActive] = useState(false);
    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);

    const activeClass = styles[`theme_${theme}Active`];
    const rootClasses = classnames(
      styles.root,
      !rounded && withSearch && styles.withSearch,
      className,
      ...getClasses(styles, { theme }),
      {
        /** react-scroll сам применит нужный класс */
        [activeClass]: !scroll && active,
      },
    );

    const startPath = useMemo(() => {
      if (!basePath || basePath.startsWith('/')) return basePath;
      return `/${basePath}`;
    }, [basePath]);

    const pathname = useMemo(() => {
      if (typeof window === 'undefined') return '';
      if (!currentPathname) return global.location.pathname;
      return startPath ? `${startPath}${currentPathname}` : currentPathname;
    }, [currentPathname, startPath]);

    useEffect(() => {
      const fullHref = startPath ? `${startPath}${href}` : href;
      const checkHrefList = [fullHref];

      if (extraHrefList && Array.isArray(extraHrefList) && extraHrefList.length > 0) {
        const withStartPath = extraHrefList.map((url) => (startPath ? `${startPath}${url}` : url));

        checkHrefList.push(...withStartPath);
      }

      setActive(
        activeProps || (exact ? checkHrefList.includes(pathname) : checkHrefList.some((url) => pathname.includes(url))),
      );
    }, [exact, href, activeProps, startPath, pathname, currentPathname, extraHrefList]);

    const dataId = `link_item_${id}`;

    return scroll ? (
      <Box ref={setWrapperRef} data-id={dataId} data-elem="menu_link_wrapper">
        <ScrollLink
          ref={ref}
          data-id={dataId}
          href={href}
          hashSpy={hashSpy}
          to={href.replace('#', '')}
          containerId={containerId}
          activeClass={activeClass}
          duration={700}
          smooth={smooth}
          spy={spy}
          target={target}
          absolute
          offset={offset}
          className={rootClasses}
          //@ts-ignore
          onClick={(e) => {
            //Создаем дефолтное события клика для юнита, т.к. ScrollLink предотвращает всплытие
            //https://centrateam.atlassian.net/browse/CA-3408
            wrapperRef?.click();
            onClick?.(e);
          }}
          onSetActive={onSetActive}
          data-menu-link="true"
        >
          {children}
        </ScrollLink>
      </Box>
    ) : (
      <Link
        onClick={onClick}
        ref={ref}
        href={href}
        id={dataId}
        target={target}
        className={classnames(rootClasses, active && activeClass)}
        noStyles
        disableNextJs={disableNextJs}
        data-menu-link="true"
      >
        {children}
      </Link>
    );
  },
);

export const SidebarMenuLink = memo(SidebarMenuLinkRef);

SidebarMenuLink.displayName = 'SidebarMenuLink';
